// src/hrms/alarms/alarmStore.ts
import type { AlarmItemKey, ItemAlarm } from "./alarmTypes";

/**
 * This browser's alarms, per signed-in account, in localStorage. Like the
 * mobile app, alarms belong to the device (a later hrms sync can carry them
 * between devices); unlike it, every open TeamLio tab reads and writes the
 * same record, so each update re-reads storage first and the `storage` event
 * tells the other tabs. An answer in one tab silences the alarm in all.
 */

const ALARMS_KEY_PREFIX = "teamlio.alarms.v1";
const RUNG_KEY_PREFIX = "teamlio.alarms.rung.v1";
/** A delivered-ring record older than this can never matter again (rings span ~1 hour at most). */
const RUNG_RETENTION_MS = 2 * 24 * 60 * 60_000;

export type AlarmMap = Record<string, ItemAlarm>;

const EMPTY: AlarmMap = Object.freeze({}) as AlarmMap;
const cache = new Map<string, { map: AlarmMap; raw: string | null }>();
const listeners = new Set<() => void>();

function storageKey(ownerId: string) {
  return `${ALARMS_KEY_PREFIX}.${encodeURIComponent(ownerId)}`;
}

function rungKey(ownerId: string) {
  return `${RUNG_KEY_PREFIX}.${encodeURIComponent(ownerId)}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isItemAlarm(value: unknown): value is ItemAlarm {
  if (!isRecord(value)) return false;
  return Array.isArray(value.alerts)
    && Array.isArray(value.followUps)
    && isRecord(value.handled)
    && isRecord(value.item)
    && typeof value.item.id === "string"
    && (value.item.source === "todo" || value.item.source === "task")
    && typeof value.enabled === "boolean"
    && typeof value.armedAt === "number"
    && typeof value.repeatEveryMinutes === "number"
    && typeof value.snoozeMinutes === "number";
}

function readRaw(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function parse(raw: string | null): AlarmMap {
  if (!raw) return EMPTY;
  try {
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value)) return EMPTY;
    return Object.fromEntries(Object.entries(value).filter(([, alarm]) => isItemAlarm(alarm))) as AlarmMap;
  } catch {
    return EMPTY;
  }
}

function emit() {
  listeners.forEach((listener) => listener());
}

export function subscribeAlarms(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The owner's alarms. The same object comes back until storage changes, so
 * React (useSyncExternalStore) only re-renders on a real change.
 */
export function loadAlarms(ownerId: string | null | undefined): AlarmMap {
  if (!ownerId) return EMPTY;
  const raw = readRaw(storageKey(ownerId));
  const cached = cache.get(ownerId);
  if (cached && cached.raw === raw) return cached.map;
  const map = parse(raw);
  cache.set(ownerId, { map, raw });
  return map;
}

/** Applies `update` to the freshest stored copy (another tab may have written since) and persists it. */
export function updateAlarms(ownerId: string, update: (current: AlarmMap) => AlarmMap): AlarmMap {
  const current = loadAlarms(ownerId);
  const next = update(current);
  if (next === current) return current;
  const raw = JSON.stringify(next);
  try {
    window.localStorage.setItem(storageKey(ownerId), raw);
    cache.set(ownerId, { map: next, raw });
  } catch {
    // Storage full or blocked: memory still holds the change for this tab.
    cache.set(ownerId, { map: next, raw: readRaw(storageKey(ownerId)) });
  }
  emit();
  return next;
}

export function updateAlarm(ownerId: string, itemKey: AlarmItemKey, update: (current: ItemAlarm | undefined) => ItemAlarm | undefined) {
  return updateAlarms(ownerId, (current) => {
    const existing = current[itemKey];
    const next = update(existing);
    if (next === existing) return current;
    const copy = { ...current };
    if (next) copy[itemKey] = next;
    else delete copy[itemKey];
    return copy;
  });
}

/**
 * Records that a ring has been delivered (system notification shown) and
 * says whether this caller is the first. Every open tab runs the engine, so
 * this is what keeps three tabs from posting three notifications. The engine
 * calls it under a Web Lock where the browser has them.
 */
export function claimRing(ownerId: string, notificationId: string, now: number): boolean {
  const key = rungKey(ownerId);
  let rung: Record<string, number> = {};
  try {
    const value: unknown = JSON.parse(readRaw(key) || "{}");
    if (isRecord(value)) rung = value as Record<string, number>;
  } catch {
    rung = {};
  }
  if (typeof rung[notificationId] === "number") return false;
  const kept = Object.fromEntries(Object.entries(rung).filter(([, at]) => typeof at === "number" && now - at < RUNG_RETENTION_MS));
  kept[notificationId] = now;
  try {
    window.localStorage.setItem(key, JSON.stringify(kept));
  } catch {
    // Unrecorded, a second tab may notify too; the shared notification tag still collapses them.
  }
  return true;
}

// Another tab wrote: drop nothing (loadAlarms compares raw strings), just tell React and the engine.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === null || event.key.startsWith(ALARMS_KEY_PREFIX)) emit();
  });
}

/** Test seam: forget everything held in memory. */
export function resetAlarmStoreMemory() {
  cache.clear();
}
